import { useState } from "react";
import { Helmet } from "react-helmet";
import { FormProvider, useForm } from "react-hook-form";
import validator from "validator";
import { gql, useMutation } from "@apollo/client";
import Layout from "../../components/Layout";
import LayoutCard from "../../components/LayoutCard";
import TextInput from "../../components/TextInput";
import { Heading, Text } from "../../components/Type";
import Button from "../../components/Button";
import { ArrowRight } from "../../components/Icons/general-interface-1";
import styles from "./index.module.scss";

const FORGOT_PASSWORD = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email) {
      ok
    }
  }
`;

const ForgotPassword = () => {
  const [data, setData] = useState();
  const [error, setError] = useState<unknown>();
  const methods = useForm<{ email: string }>({
    mode: "onTouched",
    defaultValues: {
      email: "",
    },
  });
  const [forgotPassword] = useMutation(FORGOT_PASSWORD);
  const handleSubmit = async (values: { email: string }) => {
    setError(undefined);
    try {
      const { data } = await forgotPassword({
        variables: {
          email: values.email,
        },
      });
      setData(data);
    } catch (err) {
      setError(err);
    }
  };

  const getErrorMessage = (error) => {
    return (
      error.graphQLErrors?.[0]?.extensions?.exception?.data?.data[0]
        ?.messages[0]?.message || "An unknown error has occurred"
    );
  };

  return (
    <Layout>
      <Helmet>
        <title>Motion Forgot Password</title>
      </Helmet>
      <LayoutCard
        error={error ? getErrorMessage(error) : undefined}
        header={<Heading size="h4">I forgot my password</Heading>}
        footer={
          <Text size="small">
            Don’t have an account?{" "}
            <a
              href={`mailto:${process.env.REACT_APP_MOTION_SUPPORT_EMAIL}`}
              target="_blank"
              rel="noreferrer"
            >
              Contact a Motion rep
            </a>{" "}
            to set one up.
          </Text>
        }
      >
        {data ? (
          <Text>A reset password email has been sent</Text>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <Text className={styles.descriptionText}>
                Enter your email and we’ll send you a link to reset your
                password.
              </Text>
              <TextInput
                id="email"
                label="Email"
                fieldOptions={{
                  required: "Enter your email",
                  validate: {
                    isEmail: (value) =>
                      !validator.isEmail(value as string)
                        ? "This is not a valid email"
                        : undefined,
                  },
                }}
              />
              <div className={styles.buttonRow}>
                <Button bold primary submit icon={<ArrowRight />}>
                  Reset password
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      </LayoutCard>
    </Layout>
  );
};

export default ForgotPassword;
